import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { Typography } from '@components/Typography';
import EmailTemplateBuilder from '@features/EmailTemplateBuilder';
import { usePermissions, useTranslation } from '@hooks';
import { TEMPLATE_CHANNEL_FILTER } from '@constants/campaignTemplates';
import TemplateThumbnail from '../Library/TemplateThumbnail';

const PickerRow = styled('div')({
    gridColumn: '1 / span 2',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 12,
});

const PickerCard = styled('div', {
    shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
    border: isSelected ? '2px solid var(--primary-main)' : '1px solid var(--divider)',
    borderRadius: 8,
    overflow: 'hidden',
    cursor: 'pointer',
    background: '#fff',
    '&:hover': {
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    },
}));

const CreateCard = styled('button')({
    border: '1px dashed var(--divider)',
    borderRadius: 8,
    minHeight: 160,
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: 'var(--text-secondary)',
    fontFamily: 'inherit',
    '&:hover': {
        borderColor: 'var(--primary-main)',
        color: 'var(--primary-main)',
    },
});

/**
 * Email content step: templates are the only email path. Survey/coupon campaigns fill a
 * template's dynamic-link buttons at send time, so their pickers only offer link-capable
 * templates; message campaigns have no destination to supply and only offer static ones.
 */
const TemplatePicker = ({ isView, requiresDynamicLink, requiredLinkType }) => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const { hasFullAccess } = usePermissions();
    const { data: templates } = useSelector(state => state.campaignTemplates);

    const [builderOpen, setBuilderOpen] = useState(false);

    const selectedTemplateId = watch('emailTemplateId');

    const selectableTemplates = useMemo(
        () =>
            (templates || [])
                .filter(template => template.channel === TEMPLATE_CHANNEL_FILTER.EMAIL)
                .filter(template => !template.isSystem)
                .filter(
                    template =>
                        template.id === selectedTemplateId ||
                        (!template.isArchived &&
                            template.isLinkCapable === requiresDynamicLink &&
                            (!requiresDynamicLink || template.linkType === requiredLinkType)),
                ),
        [templates, selectedTemplateId, requiresDynamicLink, requiredLinkType],
    );

    const selectTemplate = id => {
        if (isView) return;
        setValue('emailTemplateId', id, { shouldDirty: true, shouldValidate: true });
    };

    return (
        <>
            <div style={{ gridColumn: '1 / span 2' }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
                    {t('TemplateLibrary.emailDesign')} *
                </Typography>
                <Controller
                    control={control}
                    name="emailTemplateId"
                    render={({ fieldState: { error } }) => (
                        <>
                            <PickerRow>
                                {selectableTemplates.map(template => (
                                    <PickerCard
                                        key={template.id}
                                        isSelected={template.id === selectedTemplateId}
                                        onClick={() => selectTemplate(template.id)}
                                    >
                                        <TemplateThumbnail html={template.compiledHtml} title={template.name} />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                padding: '8px 12px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {template.name}
                                        </Typography>
                                    </PickerCard>
                                ))}
                                {!isView && hasFullAccess('campaigns') && (
                                    <CreateCard type="button" onClick={() => setBuilderOpen(true)}>
                                        <AddIcon />
                                        <Typography variant="caption">{t('TemplateLibrary.createNew')}</Typography>
                                    </CreateCard>
                                )}
                            </PickerRow>
                            {error ? (
                                <Typography variant="caption" sx={{ color: 'var(--error-main)' }}>
                                    {t(error.message)}
                                </Typography>
                            ) : null}
                        </>
                    )}
                />
            </div>
            {builderOpen && (
                <EmailTemplateBuilder
                    isOpen={builderOpen}
                    onClose={() => setBuilderOpen(false)}
                    template={null}
                    isView={false}
                    onSaved={saved => selectTemplate(saved.id)}
                />
            )}
        </>
    );
};

export default TemplatePicker;
